// Lot-value aggregation for the AI value summaries: compares the dealer's
// asking prices (Best_Sell_Price) with the AI price model (PredictedPrice).
//
// Both totals are summed over the SAME set of units — those that have a valid
// asking price AND a valid AI price — so the gap never mixes in units that
// only have one side of the comparison.

// Gap-severity thresholds (fraction of AI value): the built-in default scale.
// Each dealership can override it (pricing.gap_* in dealership settings,
// useDealershipSettings().gapThresholds); every function below takes the
// scale as its last argument and falls back to these defaults.
export const GAP_THRESHOLDS = Object.freeze({ neutral: 0.02, warn: 0.05, critical: 0.1 });

// Tooltip wording shared by the lot value tile and the branch AI value table,
// so both explain Asking / AI value / Gap identically.
export const LOT_VALUE_TIPS = Object.freeze({
    asking: 'List prices in the stock system, for units that also have an AI price',
    ai: 'Model estimate of the net sale price, same units',
    gap: 'Asking minus AI value. Positive means listed above the AI estimate',
});

const UNKNOWN_BRANCH = 'Unknown';
const TOTAL_LABEL = 'Dealership';

// Positive, finite number or null. Strings, 0, negatives, NaN, Infinity → null.
const positivePrice = (value) =>
    (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null);

export const summarizeLotValue = (units) => {
    const list = Array.isArray(units) ? units : [];
    let pricedCount = 0;
    let askingTotal = 0;
    let aiTotal = 0;

    list.forEach((unit) => {
        const asking = positivePrice(unit?.Best_Sell_Price);
        const ai = positivePrice(unit?.PredictedPrice);
        if (asking === null || ai === null) return;
        pricedCount += 1;
        askingTotal += asking;
        aiTotal += ai;
    });

    return {
        unitCount: list.length,
        pricedCount,
        askingTotal,
        aiTotal,
        gapTotal: askingTotal - aiTotal,
        gapPct: aiTotal > 0 ? askingTotal / aiTotal - 1 : null,
    };
};

export const summarizeByBranch = (units) => {
    const list = Array.isArray(units) ? units : [];
    const groups = new Map();

    list.forEach((unit) => {
        const branch = unit?.BranchName || UNKNOWN_BRANCH;
        if (!groups.has(branch)) groups.set(branch, []);
        groups.get(branch).push(unit);
    });

    const rows = Array.from(groups, ([branch, branchUnits]) => ({
        branch,
        ...summarizeLotValue(branchUnits),
    })).sort((a, b) => b.gapTotal - a.gapTotal);

    return { rows, total: { branch: TOTAL_LABEL, ...summarizeLotValue(list) } };
};

// Severity is judged on the gap AS DISPLAYED (one-decimal percent, e.g.
// "+10.0%"), so the colour always agrees with the number the dealer reads:
// 10.03% shows as "+10.0%" and is therefore not "above 10%".
// Compared in integer tenths of a percent to avoid float edge cases.
// A usable scale: the one given, else the defaults (so a stray argument,
// e.g. an array index from .map, can never break the colours).
const scaleOf = (t) => (t && typeof t === 'object' && typeof t.warn === 'number' ? t : GAP_THRESHOLDS);

const toTenthsOfPct = (fraction) => Math.round(Number((fraction * 100).toFixed(1)) * 10);

export const gapSeverity = (gapPct, thresholds = GAP_THRESHOLDS) => {
    if (gapPct === null || gapPct === undefined || Number.isNaN(gapPct)) return null;
    const t = scaleOf(thresholds);
    const shown = toTenthsOfPct(gapPct);
    if (Math.abs(shown) <= toTenthsOfPct(t.neutral)) return 'neutral';
    if (shown > toTenthsOfPct(t.critical)) return 'critical';
    if (shown > toTenthsOfPct(t.warn)) return 'warn';
    return 'ok';
};

// Direction of one unit's asking price against its AI price, on the SAME
// scale as gapSeverity: above the warn / critical thresholds → 'above' /
// 'wellAbove'; more than warn below → 'below'; anything else → 'inline'.
// Judged on the displayed one-decimal percent, like gapSeverity.
export const gapDirection = (gapPct, thresholds = GAP_THRESHOLDS) => {
    const t = scaleOf(thresholds);
    const severity = gapSeverity(gapPct, t);
    if (severity === null) return null;
    if (severity === 'critical') return 'wellAbove';
    if (severity === 'warn') return 'above';
    if (toTenthsOfPct(gapPct) < -toTenthsOfPct(t.warn)) return 'below';
    return 'inline';
};

// "+5.8%" / "−3.1%" (U+2212 minus, matching formatMoneyShort); fraction in.
// A value that rounds to zero shows unsigned "0.0%"; missing → "".
export const formatPctSigned = (fraction) => {
    if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return '';
    const pct = Math.abs(fraction * 100).toFixed(1);
    if (pct === '0.0') return '0.0%';
    return `${fraction < 0 ? '−' : '+'}${pct}%`;
};

// "$93.2M" / "$885k" / "$950"; negatives get a U+2212 minus; missing → "—".
export const formatMoneyShort = (value) => {
    if (value === null || value === undefined || typeof value !== 'number' || Number.isNaN(value)) {
        return '—';
    }
    const sign = value < 0 ? '−' : '';
    const abs = Math.abs(value);

    let body;
    if (abs >= 1e6 || Math.round(abs / 1e3) >= 1000) {
        body = `${(abs / 1e6).toFixed(1)}M`;
    } else if (abs >= 1e3 || Math.round(abs) >= 1000) {
        body = `${Math.round(abs / 1e3)}k`;
    } else {
        body = `${Math.round(abs)}`;
    }
    return body === '0' ? '$0' : `${sign}$${body}`;
};

// ── Used lot value tile (shared by the v1 tile and the v2 KPI band) ───────

export const LOT_EMPTY_TEXT = Object.freeze({
    noUsed: 'AI prices cover used units only',
    noPaired: 'No units with both an asking price and an AI price',
});

const lotUnitType = (unit) => unit?.NewUsedStatus ?? unit?.TYPE;

// "+$5.0M" for a positive gap; formatMoneyShort otherwise.
export const formatMoneySigned = (value) => {
    const text = formatMoneyShort(value);
    return value > 0 && text !== '$0' ? `+${text}` : text;
};

// "above AI estimate" / "below AI estimate"; nothing at 0.0%.
export const lotGapHint = (gapTotal, pctText) => {
    if (pctText === '0.0%' || !gapTotal) return null;
    return gapTotal > 0 ? 'above AI estimate' : 'below AI estimate';
};

// Coverage note for the (i): how many used units feed the figures.
export const lotCoverageText = ({ pricedCount, usedCount, hasNew }) => {
    const noun = usedCount === 1 ? 'unit' : 'units';
    const base = `Based on ${pricedCount} of ${usedCount} used ${noun} with both an asking price and an AI price.`;
    return hasNew ? `${base} New units have no AI price.` : base;
};

// Everything the lot value tile shows. AI prices exist for used units only
// (same rule as the branch table), so the figures summarise used units.
export const buildLotValueView = (units, thresholds = GAP_THRESHOLDS) => {
    const list = Array.isArray(units) ? units : [];
    const used = list.filter((u) => lotUnitType(u) === 'U');
    const summary = summarizeLotValue(used);
    const hasNew = list.some((u) => lotUnitType(u) === 'N');
    const pctText = formatPctSigned(summary.gapPct);
    let emptyText = null;
    if (used.length === 0) emptyText = LOT_EMPTY_TEXT.noUsed;
    else if (summary.pricedCount === 0) emptyText = LOT_EMPTY_TEXT.noPaired;
    return {
        ...summary,
        usedCount: used.length,
        hasNew,
        pctText,
        hint: lotGapHint(summary.gapTotal, pctText),
        severity: gapSeverity(summary.gapPct, thresholds) || 'neutral',
        emptyText,
    };
};
